import React, { useCallback } from 'react'
import { Image } from '@/atoms/Image'
import { CaptionMD, HeadingMD, TitleMD, ParagraphSM } from '@/atoms/Text'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { TorchSubmitButton } from '../TorchSubmitButton'

interface BenefitCardProps {
  title: string
  description: string
  imageUrl: string
  alt: string
}

const BenefitCard: React.FC<BenefitCardProps> = ({ title, description, imageUrl, alt }) => {
  return (
    <div className="flex flex-col p-6 md:p-8">
      <div className="mb-6 flex justify-center">
        <Image alt={alt} className="w-full" src={getCloudinaryImageUrl({ path: imageUrl })} width={300} height={200} />
      </div>
      <div className="text-center">
        <TitleMD weight="bold" color="white" className="mb-3 text-start">
          {title}
        </TitleMD>
        <ParagraphSM weight="normal" color="core-gray-400" className="text-start leading-relaxed">
          {description}
        </ParagraphSM>
      </div>
    </div>
  )
}

export const SubmissionBonusesSection: React.FC = () => {
  const { track } = useSafeAnalytics()
  const { t } = useTranslate('create')

  const handleGetStartedClicked = useCallback(() => {
    track('Guild Submit Project Clicked')
  }, [track])

  return (
    <section
      className="w-full py-16 md:py-24"
      style={{
        backgroundImage: 'linear-gradient(130deg, #232323 0%, #141414 100%)',
      }}
    >
      <div className="mx-auto max-w-[1440px] px-4 md:px-12 lg:px-[120px]">
        <HeadingMD weight="bold" color="white" className="md:photon-heading-xl lg:photon-display mb-12 text-center">
          {t('whatYourSubmissionGetsYou', 'What Your Submission Gets You')}
        </HeadingMD>

        <div className="mb-12 grid md:grid-cols-2">
          <BenefitCard
            title={t('finalGuildScore', 'Final Guild Score')}
            description={t('theGuildWillScore', 'The Guild will score your project from 0-100.')}
            imageUrl="v1753123295/angel-app/guild/submit-screen/guild-score-info.png"
            alt={t('guildScoreGraphic', 'Guild Score Gauge')}
          />
          <BenefitCard
            title={t('guildFeedback', 'Guild Feedback')}
            description={t('guildMembersWillGive', 'Guild members will give you suggestions for improvement.')}
            imageUrl="v1753123293/angel-app/guild/submit-screen/guild-feedback-info.png"
            alt={t('guildFeedbackGraphic', 'Guild Feedback Interface')}
          />
          <BenefitCard
            title={t('viewerRetention', 'Viewer Retention')}
            description={t('seeWhereViewersStop', 'See where viewers stop watching your submission.')}
            imageUrl="v1753123784/angel-app/guild/submit-screen/viewer-retention-info.png"
            alt={t('viewerRetentionGraphic', 'Viewer Retention Graph')}
          />
          <BenefitCard
            title={t('fiveStarRatings', 'Five Star Ratings')}
            description={t('identifyAreasOfWeakness', 'Identify areas of weakness and strength in your submission.')}
            imageUrl="v1753123297/angel-app/guild/submit-screen/star-rating-info.png"
            alt={t('starRatingsGraphic', 'Five Star Rating System')}
          />
        </div>

        <div className="flex flex-col items-center gap-4">
          <TorchSubmitButton handleSubmitClicked={handleGetStartedClicked} isDark={true} />
          <CaptionMD weight="normal" color="core-gray-500">
            {t('applicationFee', '$250 application fee')}
          </CaptionMD>
        </div>
      </div>
    </section>
  )
}
